import * as React from "react";
import {
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  useReactTable,
} from "@tanstack/react-table";
import {
  CheckCircle2,
  Clock3,
  CircleAlert,
  MoreHorizontal,
  Trash2,
} from "lucide-react";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatCurrency, formatDate } from "@/lib/format";
import type { SavingsProspect } from "./types";

export function reviewStatus(prospect: SavingsProspect) {
  return prospect.stale
    ? "Refresh required"
    : prospect.eligible
      ? "Estimate ready"
      : "Needs review";
}

export function ReviewStatus({ prospect }: { prospect: SavingsProspect }) {
  const ready = prospect.eligible && !prospect.stale;
  const Icon = prospect.stale ? Clock3 : ready ? CheckCircle2 : CircleAlert;
  return (
    <Badge
      variant="outline"
      className={
        ready
          ? "border-emerald-600/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
          : "border-amber-600/20 bg-amber-500/10 text-amber-800 dark:text-amber-400"
      }
    >
      <Icon aria-hidden="true" />
      {reviewStatus(prospect)}
    </Badge>
  );
}

export function CampaignClientsTable({
  prospects,
  onReview,
  onRemove,
  disabled = false,
}: {
  prospects: SavingsProspect[];
  onReview: (customerId: string) => void;
  onRemove?: (prospect: SavingsProspect) => void;
  disabled?: boolean;
}) {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const columns = React.useMemo<ColumnDef<SavingsProspect>[]>(
    () => [
      {
        id: "client",
        accessorFn: (p) => `${p.customerName} ${p.email ?? ""}`,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Client" />
        ),
        meta: {
          label: "Client",
          variant: "text",
          placeholder: "Search clients…",
        },
        cell: ({ row }) => (
          <div className="min-w-44">
            <Button
              variant="link"
              className="h-auto p-0 text-left font-medium"
              disabled={disabled}
              onClick={() => onReview(row.original.customerId)}
            >
              {row.original.customerName}
            </Button>
            <p className="mt-1 text-xs text-muted-foreground">
              Updated {formatDate(row.original.generatedAt)}
            </p>
          </div>
        ),
      },
      {
        id: "coffees",
        accessorFn: (p) => p.coffees.map((c) => c.productName).join(" · "),
        header: "Top coffees",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <div className="max-w-64 whitespace-normal text-sm">
            {row.original.coffees.length ? (
              row.original.coffees.map((c, index) => (
                <p key={`${c.productId}-${c.historicalCargoId ?? index}`}>{c.productName}</p>
              ))
            ) : (
              <span className="text-muted-foreground">No purchases</span>
            )}
          </div>
        ),
      },
      {
        id: "rep",
        accessorFn: (p) => p.salesRepName ?? "Unassigned",
        header: "Sales rep",
        enableColumnFilter: false,
      },
      {
        id: "savings",
        accessorFn: (p) => (p.stale ? null : p.estimatedSavings),
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Est. savings" />
        ),
        enableColumnFilter: false,
        cell: ({ getValue }) => (
          <span className="font-medium tabular-nums">
            {getValue() === null ? "—" : formatCurrency(getValue() as number)}
          </span>
        ),
      },
      {
        id: "review",
        accessorFn: reviewStatus,
        header: "Review status",
        meta: {
          label: "Review status",
          variant: "multiSelect",
          options: ["Estimate ready", "Needs review", "Refresh required"].map(
            (value) => ({ label: value, value }),
          ),
        },
        filterFn: (row, id, values: string[]) =>
          !values?.length || values.includes(row.getValue(id)),
        cell: ({ row }) => <ReviewStatus prospect={row.original} />,
      },
      {
        id: "response",
        accessorFn: () => "Not contacted",
        header: "Response",
        enableColumnFilter: false,
        cell: () => (
          <Badge variant="secondary">
            <Clock3 aria-hidden="true" />
            Not contacted
          </Badge>
        ),
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        enableColumnFilter: false,
        enableHiding: false,
        cell: ({ row }) => (
          <div
            className="flex items-center justify-end gap-1"
            onClick={(event) => event.stopPropagation()}
          >
            <Button
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() => onReview(row.original.customerId)}
              aria-label={`Review ${row.original.customerName}`}
            >
              Review
            </Button>
            {onRemove && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={disabled}
                    aria-label={`Actions for ${row.original.customerName}`}
                  >
                    <MoreHorizontal aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={() => onRemove(row.original)}
                  >
                    <Trash2 aria-hidden="true" />
                    Remove from campaign list
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        ),
      },
    ],
    [onReview, onRemove, disabled],
  );
  const table = useReactTable({
    data: prospects,
    columns,
    state: { columnFilters, sorting, pagination },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getRowId: (p) => p.customerId,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    enableRowSelection: false,
  });
  return (
    <DataTable
      table={table}
      onRowClick={(row) => {
        if (!disabled) onReview(row.original.customerId);
      }}
    >
      <DataTableToolbar table={table} />
    </DataTable>
  );
}
